import {THEME} from '@wandb/weave/common/css/color.styles';
import * as globals from '@wandb/weave/common/css/globals.styles';
import React from 'react';
import {
  FlexibleXYPlot,
  Hint,
  HorizontalGridLines,
  LineSeries,
  MarkSeries,
  XAxis,
  YAxis,
} from 'react-vis';

import {formatYAxis} from '../../util/plotHelpers/axis';
import {
  getAxisStyleForFontSize,
  getPlotFontSize,
} from '../../util/plotHelpers/plotFontSize';
import {getPlotMargin} from '../../util/plotHelpers/plotHelpers';
import {
  createArtifactVersionMetricHintFormatter,
  createArtifactVersionMetricXTickFormatter,
  getArtifactVersionMetricVersionXTickValues,
} from './artifactVersionMetricChartFormatting';
import type {ArtifactVersionMetricPlotReady} from './artifactVersionMetricPlotModel';

const GRID_LINE_STYLE = {
  stroke: THEME.border,
  strokeWidth: 1,
  opacity: 0.35,
};
const LINE_STROKE_COLOR = globals.TEAL_550;
const MARK_SIZE = 4;

type ArtifactVersionMetricXYPlotFrameProps = {
  plot: ArtifactVersionMetricPlotReady;
  width: number;
  height: number;
  /** Connect the points with a line (line chart) vs. points only (scatter). */
  connectPoints: boolean;
};

/**
 * Shared react-vis scaffolding for the line and scatter charts, which differ
 * only by whether the points are joined by a line. The line and scatter
 * components are thin wrappers over this frame.
 */
export const ArtifactVersionMetricXYPlotFrame: React.FC<
  ArtifactVersionMetricXYPlotFrameProps
> = ({plot, width, height, connectPoints}) => {
  const {config, points, xDomain, yDomain} = plot;
  const [hoveredPoint, setHoveredPoint] = React.useState<
    (typeof points)[number] | null
  >(null);

  const handlePointMouseOver = React.useCallback(
    (point: unknown) => {
      setHoveredPoint(point as (typeof points)[number]);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const handlePointMouseOut = React.useCallback(() => {
    setHoveredPoint(null);
  }, []);

  if (xDomain == null || points.length === 0) {
    return null;
  }

  const fontSize = getPlotFontSize('auto', height);
  const fontStyles = {
    ...getAxisStyleForFontSize(fontSize),
    fill: THEME.textTertiary,
  };
  const yScale = config.yLogScale === true ? 'log' : 'linear';
  // X is a metric only for scatter; for line it is version-derived (linear).
  const xScale =
    config.chartType === 'scatter' && config.xLogScale === true
      ? 'log'
      : 'linear';
  const formatXTick = createArtifactVersionMetricXTickFormatter(plot);
  const formatHint = createArtifactVersionMetricHintFormatter(plot);
  const versionXTickValues = getArtifactVersionMetricVersionXTickValues(plot);
  const colorDomain = points.map(point => point.uniqueId);
  const colorRange = points.map(point => point.color);
  const plotMargin = getPlotMargin({
    axisDomain: {yAxis: yDomain, xAxis: xDomain},
    axisType: {yAxis: yScale, xAxis: xScale},
    fontSize,
    rotateXAxisTickLabels: false,
    panelHeight: height,
  });

  // CanvasWrapper.css clips every desktop react-vis plot by default.
  // Hints need to escape the plot bounds while remaining point-anchored.
  return (
    <FlexibleXYPlot
      width={width}
      height={height}
      className="!overflow-visible"
      margin={plotMargin}
      xDomain={xDomain}
      yDomain={yDomain}
      xType={xScale}
      yType={yScale}>
      <HorizontalGridLines style={GRID_LINE_STYLE} />
      <XAxis
        style={fontStyles}
        tickFormat={formatXTick as (value: number) => string}
        tickValues={versionXTickValues}
      />
      <YAxis
        style={fontStyles}
        tickFormat={formatYAxis as (value: number) => string}
      />
      {connectPoints &&
        plot.lineSegments.map((lineSeriesData, index) => (
          <LineSeries
            // oxlint-disable-next-line react/no-array-index-key
            key={index}
            data={lineSeriesData}
            color={LINE_STROKE_COLOR}
          />
        ))}
      <MarkSeries
        data={points}
        colorType="literal"
        colorDomain={colorDomain}
        colorRange={colorRange}
        size={MARK_SIZE}
        onValueMouseOver={handlePointMouseOver}
        onValueMouseOut={handlePointMouseOut}
      />
      {hoveredPoint?.artifactRow != null && (
        <Hint
          className="z-[2]"
          value={hoveredPoint}
          format={formatHint as any}
        />
      )}
    </FlexibleXYPlot>
  );
};
